'use client';
import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '@/lib/LanguageContext';

interface Metric {
  valueEs: string;
  valueFr: string;
  suffix: string;
  labelEs: string;
  labelFr: string;
  target: number;
  decimals?: number;
}

const metrics: Metric[] = [
{
  valueEs: '10',
  valueFr: '10',
  suffix: '+',
  labelEs: 'Años de Experiencia',
  labelFr: 'Années d\'Expérience',
  target: 10
},
{
  valueEs: '200',
  valueFr: '200',
  suffix: '+',
  labelEs: 'Clientes Atendidos',
  labelFr: 'Clients Servis',
  target: 200
},
{
  valueEs: '9',
  valueFr: '9',
  suffix: '',
  labelEs: 'Servicios Especializados',
  labelFr: 'Services Spécialisés',
  target: 9
}];


const TechPurposeSection: React.FC = () => {
  const { language } = useLanguage();
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [counts, setCounts] = useState(metrics.map(() => 0));

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    const duration = 1400;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);

      setCounts(metrics.map((m) => Math.floor(eased * m.target)));

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCounts(metrics.map((m) => m.target));
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible]);

  const title = language === 'fr' ? 'Technologie avec Objectif' : 'Tecnología con Propósito';
  const subtitle = language === 'fr' ? 'Votre monde est déjà numérique. Nous le rendons sûr, efficace et durable. Nous transformons les processus en opportunités. Bienvenue dans le futur avec INNOBOX.' : 'Tu mundo ya es digital. Nosotros lo hacemos seguro, eficiente y sostenible. Transformamos procesos en oportunidades. Bienvenido al futuro con INNOBOX.';
  const ctaLabel = language === 'fr' ? 'Notre Infrastructure' : 'Nuestra Infraestructura';

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden"
      style={{ backgroundColor: '#141618', paddingTop: '120px', paddingBottom: '120px' }}>
      
      {/* Grid lines */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 bottom-0 left-[25%] w-px bg-white/5" />
        <div className="absolute top-0 bottom-0 left-[50%] w-px bg-white/5" />
        <div className="absolute top-0 bottom-0 left-[75%] w-px bg-white/5" />
        <div className="absolute left-0 right-0 top-0 h-px bg-white/6" />
        <div className="absolute left-0 right-0 bottom-0 h-px bg-white/6" />
      </div>

      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://innobox.cl/wp-content/uploads/2021/07/2-940x529.png"
          alt="Infraestructura tecnológica Innobox"
          className="w-full h-full object-cover"
          style={{ opacity: 0.04 }} />
        
      </div>

      <div className="max-w-[1280px] mx-auto px-6 relative z-10">
        {/* Heading */}
        <div
          className={`text-center mb-16 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`
          }>
          <p
            className="text-white/60 mb-6 max-w-2xl mx-auto"
            style={{ fontSize: '19px', lineHeight: 1.6 }}>
            {subtitle}
          </p>
          <h2
            className="text-white"
            style={{
              fontSize: 'clamp(28px, 3.5vw, 52px)',
              fontWeight: 400,
              lineHeight: 1.05,
              letterSpacing: '-0.01em'
            }}>
            {title}
          </h2>
        </div>

        {/* Metrics grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-0 mb-16">
          {metrics.map((metric, index) => {
            const label = language === 'fr' ? metric.labelFr : metric.labelEs;
            return (
              <div
                key={index}
                className="cipher-metric-card group"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                  transition: `opacity 0.7s ease ${index * 150}ms, transform 0.7s ease ${index * 150}ms`
                }}>
                {/* Corner dots */}
                <div className="metric-dot" style={{ top: '-3px', left: '-3px' }} />
                <div className="metric-dot" style={{ top: '-3px', right: '-3px' }} />
                <div className="metric-dot" style={{ bottom: '-3px', left: '-3px' }} />
                <div className="metric-dot" style={{ bottom: '-3px', right: '-3px' }} />
                {/* Top accent line */}
                <div className="metric-line-top" />

                <div className="flex items-baseline gap-1 mb-2">
                  <span
                    className="text-white"
                    style={{ fontSize: 'clamp(40px, 5vw, 64px)', fontWeight: 400, letterSpacing: '-0.02em', lineHeight: 1 }}>
                    {counts[index]}
                  </span>
                  <span className="text-[#abd233]" style={{ fontSize: '24px', fontWeight: 400 }}>
                    {metric.suffix}
                  </span>
                </div>
                <div className="text-white/40" style={{ fontSize: '13px', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                  {label}
                </div>
              </div>);

          })}
        </div>

        {/* CTA */}
        <div
          className={`flex justify-center transition-all duration-700 delay-500 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`
          }>
          <a
            href="/servicios/infraestructura-redes"
            className="group relative inline-flex items-center gap-3 overflow-hidden"
            style={{
              border: '1px solid rgba(255,255,255,0.2)',
              padding: '14px 28px',
              fontSize: '13px',
              color: '#ffffff',
              letterSpacing: '0.02em',
              transition: 'all 300ms ease-out'
            }}>
            <span
              className="absolute inset-0 bg-white transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300"
              style={{ zIndex: 0 }} />
            
            <span className="relative z-10 transition-colors duration-300 group-hover:text-[#141618]">
              {ctaLabel}
            </span>
            <svg className="relative z-10 transition-colors duration-300 group-hover:text-[#141618]" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 7h12M8 2l5 5-5 5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        </div>
      </div>
    </section>);

};

export default TechPurposeSection;